/**
 * End-to-End Encryption utilities using Web Crypto API
 * Uses ECDH (P-256) for key exchange and AES-GCM for message encryption
 * 
 * SECURITY: Keys are now stored in IndexedDB with encryption,
 * not in localStorage (which is vulnerable to XSS)
 */

import {
  storeKeyPairSecure,
  getStoredPrivateKeySecure,
  getStoredPublicKeySecure,
  hasStoredKeysSecure,
  clearStoredKeysSecure,
  migrateFromLocalStorage,
} from './secure-key-storage';

import { arrayBufferToBase64, base64ToArrayBuffer } from './crypto-utils';

// Generate a new ECDH key pair
export async function generateKeyPair(): Promise<{
  publicKey: string;
  privateKey: string;
}> {
  const keyPair = await crypto.subtle.generateKey(
    {
      name: 'ECDH',
      namedCurve: 'P-256',
    },
    true, // extractable
    ['deriveKey', 'deriveBits']
  );

  const publicKeyRaw = await crypto.subtle.exportKey('spki', keyPair.publicKey);
  const privateKeyRaw = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey);

  return {
    publicKey: arrayBufferToBase64(publicKeyRaw),
    privateKey: arrayBufferToBase64(privateKeyRaw),
  };
}

// Import a public key from base64 string
async function importPublicKey(publicKeyBase64: string): Promise<CryptoKey> {
  const keyData = base64ToArrayBuffer(publicKeyBase64);
  return crypto.subtle.importKey(
    'spki',
    keyData,
    {
      name: 'ECDH',
      namedCurve: 'P-256',
    },
    false,
    []
  );
}

// Import a private key from base64 string
async function importPrivateKey(privateKeyBase64: string): Promise<CryptoKey> {
  const keyData = base64ToArrayBuffer(privateKeyBase64);
  return crypto.subtle.importKey(
    'pkcs8',
    keyData,
    {
      name: 'ECDH',
      namedCurve: 'P-256',
    },
    false,
    ['deriveKey', 'deriveBits']
  );
}

// Derive a shared AES-GCM key from ECDH key exchange
export async function deriveSharedSecret(
  myPrivateKey: string,
  theirPublicKey: string
): Promise<CryptoKey> {
  const privateKey = await importPrivateKey(myPrivateKey);
  const publicKey = await importPublicKey(theirPublicKey);

  return crypto.subtle.deriveKey(
    {
      name: 'ECDH',
      public: publicKey,
    },
    privateKey,
    {
      name: 'AES-GCM',
      length: 256,
    },
    false,
    ['encrypt', 'decrypt']
  );
}

// Encrypt a message using AES-GCM
export async function encryptMessage(
  content: string,
  sharedSecret: CryptoKey
): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  
  // Generate random IV (12 bytes for AES-GCM)
  const iv = crypto.getRandomValues(new Uint8Array(12));
  
  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    sharedSecret,
    data
  );

  // Combine IV + ciphertext
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);

  return arrayBufferToBase64(combined.buffer);
}

// Decrypt a message using AES-GCM
export async function decryptMessage(
  ciphertext: string,
  sharedSecret: CryptoKey
): Promise<string> {
  const combined = new Uint8Array(base64ToArrayBuffer(ciphertext));
  
  // Extract IV (first 12 bytes) and ciphertext
  const iv = combined.slice(0, 12);
  const encryptedData = combined.slice(12);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    sharedSecret,
    encryptedData
  );

  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}

// Module-level flag: only attempt localStorage migration once per session
let migrationAttempted = false;

async function ensureMigration(): Promise<void> {
  if (migrationAttempted) return;
  migrationAttempted = true;
  await migrateFromLocalStorage();
}

/**
 * Store keys securely using IndexedDB with encryption
 * Much more secure than localStorage (protected from XSS)
 */
export async function storeKeyPair(publicKey: string, privateKey: string): Promise<void> {
  await storeKeyPairSecure(publicKey, privateKey);
}

/**
 * Retrieve stored private key from secure storage
 */
export async function getStoredPrivateKey(): Promise<string | null> {
  await ensureMigration();
  return getStoredPrivateKeySecure();
}

/**
 * Retrieve stored public key from secure storage
 */
export async function getStoredPublicKey(): Promise<string | null> {
  await ensureMigration();
  return getStoredPublicKeySecure();
}

/**
 * Check if keys are already generated and stored
 */
export async function hasStoredKeys(): Promise<boolean> {
  // Also check localStorage for migration purposes
  const hasLocal = !!(
    localStorage.getItem('elefa_encryption_private_key') &&
    localStorage.getItem('elefa_encryption_public_key')
  );
  
  if (hasLocal && !migrationAttempted) {
    migrationAttempted = true;
    await migrateFromLocalStorage();
  }
  
  return hasStoredKeysSecure();
}

/**
 * Clear stored keys (for logout)
 */
export async function clearStoredKeys(): Promise<void> {
  await clearStoredKeysSecure();
  // Also clear any legacy localStorage keys
  localStorage.removeItem('elefa_encryption_private_key');
  localStorage.removeItem('elefa_encryption_public_key');
}
